import React from 'react';
import TreatedBadge from './TreatedBadge.jsx';
import {
  CHART_SHADES, CONDITION, PLANNED_RING, TOOTH_COLORS, crownRingTint,
  hasUndrawnCondition, isMissing, mesialIsScreenRight, surfaceColor, surfacesFor,
  tintingCondition, toothOutline,
} from '../../lib/teeth.js';
import {
  crownPath, crownRingPath, crownSize, cusps, fissurePath, fractureGlyph,
  implantGlyph, postCoreGlyph, rootCanalGlyph, surfaceRegions, toothType,
  veneerArcPath,
} from './geometry.js';
import { listSeparator } from '../../i18n/format';

// One tooth, drawn from the OCCLUSAL view with its real crown silhouette.
//
// The tooth is drawn in a LOCAL frame — facial always toward -y — and the arch
// rotates the whole group into place (see geometry.js). That is what keeps the
// surfaces anatomically correct all the way round the curve: buccal keeps
// facing out of the mouth and mesial keeps pointing toward the midline, at
// every position. Laying flat boxes in a row and flipping labels per quadrant
// is how charts end up with half their surfaces mislabelled.
//
// MESIAL IS NOT A FIXED SIDE. It means "toward the midline", so which local
// side is mesial depends on the quadrant — `mesialIsScreenRight` decides, and
// the geometry only ever speaks of `left`/`right`.
//
// The five surfaces keep the familiar charting-box topology, sized to this
// tooth and CLIPPED to its outline, so the anatomical shape costs no hit area.

export default function Tooth({
  fdi,
  state,
  selected = false,
  hovered = false,
  // A tooth this SCREEN has something to say about — the visit workspace marks
  // the teeth a طرح درمان touches (dent-visit.md §3.1.3). It is deliberately
  // not a `state` value: nothing about it is recorded on the tooth, it is a
  // caller's annotation, and folding it into the chart's clinical vocabulary
  // would put "planned" beside "carious" as if the two were the same kind of
  // fact.
  highlighted = false,
  // How many treatments have been RECORDED on this tooth — «درمان‌شده».
  //
  // A second annotation beside `highlighted`, and the same kind of thing: a
  // caller's statement about the tooth, never a fact stored on it. The two
  // answer different questions and routinely both apply:
  //
  //   highlighted  something is PLANNED here           (dashed ring)
  //   treatedCount something was DONE here             (check badge)
  //
  // It is a COUNT rather than a boolean because a course is several sessions
  // on one tooth and «۳» is the more useful thing to read at a glance.
  treatedCount = 0,
  onSelect,
  onSurfaceSelect,
  onHover,
  onHoverEnd,
  t,
  // Per-chart-instance id namespace (Odontogram threads its own). Ids must be
  // unique per DOCUMENT, not per chart: with two charts on one page a bare
  // `crown-11` clip or `crownShine` gradient resolves to whichever chart
  // rendered first, silently cross-wiring their defs.
  idPrefix = '',
}) {
  const clipId = `${idPrefix}crown-${fdi}`;
  const { w, h } = crownSize(fdi);
  const path = crownPath(fdi);
  const regions = surfaceRegions(fdi);
  const marks = cusps(fdi);

  // Which surface the cursor is on, for the stroke highlight. Local state on
  // purpose: hover feedback is this tooth's business, not the chart's.
  const [hoverSurface, setHoverSurface] = React.useState(null);

  const available = state?.surfacesAvailable?.length
    ? state.surfacesAvailable
    : surfacesFor(fdi);
  const centre = available.includes('O') ? 'O' : 'I';

  const missing = isMissing(state?.presence);
  const outline = toothOutline(state);
  // A whole-tooth condition is carrying the outline colour, so the silhouette
  // is saying something clinical and gets the weight to be seen saying it.
  const tinted = tintingCondition(state);

  // local left/right → mesial/distal
  const mesialRight = mesialIsScreenRight(fdi);
  const byRegion = {
    facial: 'F',
    lingual: 'L',
    right: mesialRight ? 'M' : 'D',
    left: mesialRight ? 'D' : 'M',
  };

  const valueOf = (code) =>
    (state?.surfaces || []).find((s) => s.surface === code)?.value || null;

  // Untinted surfaces are TRANSPARENT so the shared crown gradient (one def in
  // Odontogram.jsx) shows through; a recorded state paints opaquely ABOVE it,
  // which is what keeps caries-red fully readable over the shading.
  const fillFor = (code) => {
    const v = valueOf(code);
    return v ? surfaceColor(v).fill : 'transparent';
  };

  const condValues = (state?.conditions || []).map((c) => c.value);
  const has = (v) => condValues.includes(v);
  const crownMaterial =
    (state?.conditions || []).find((c) => c.value === CONDITION.CROWN)?.material || null;
  const anyGlyph = has(CONDITION.CROWN) || has(CONDITION.ROOT_CANAL)
    || has(CONDITION.IMPLANT) || has(CONDITION.POST_CORE)
    || has(CONDITION.VENEER) || has(CONDITION.FRACTURE);

  const handleSurface = (code) => (e) => {
    e.stopPropagation();          // a surface click is not also a tooth click
    onSurfaceSelect?.(fdi, code);
  };

  const hoverProps = {
    onMouseMove: (e) => onHover?.(fdi, { clientX: e.clientX, clientY: e.clientY }),
    onMouseLeave: () => onHoverEnd?.(fdi),
  };

  return (
    <g
      style={{ cursor: missing ? 'default' : 'pointer' }}
      onClick={() => onSelect?.(fdi)}
      // Deliberately NOT a tab stop: 52 focusable teeth was a keyboard trap
      // in practice. The chart <svg> is ONE tab stop and arrow keys move the
      // cursor inside it (Odontogram.jsx). The aria-label stays so the group
      // still names itself to assistive tech.
      role="button"
      aria-label={[
        `${t('chart.tooth')} ${fdi}`,
        highlighted ? t('chart.annotation.planned') : null,
        treatedCount > 0 ? t('chart.annotation.treated') : null,
      ].filter(Boolean).join(listSeparator())}
      {...hoverProps}
    >
      <defs>
        <clipPath id={clipId}>
          <path d={path} />
        </clipPath>
      </defs>

      {/* Hover/selection halo, drawn BEHIND so it never covers a surface.
          pointerEvents:none as well: "behind" only holds WITHIN this tooth —
          the halo overhangs the silhouette, and against a neighbour drawn
          earlier it sits ON TOP in document order, where its stroke would
          swallow clicks meant for that neighbour's surfaces. */}
      {(selected || hovered) && (
        <path
          d={path}
          fill="none"
          stroke={selected ? '#0EA5A0' : '#94a3b8'}
          strokeWidth={selected ? 6 : 4}
          strokeOpacity={selected ? 0.35 : 0.25}
          pointerEvents="none"
        />
      )}

      {/* The planned-treatment marker: a DASHED outline, drawn with the halo
          and never instead of it. Dashed rather than a second solid ring
          because a solid one at another colour reads as a recorded finding —
          and this tooth may have none. The dash says "intended, not done",
          which is exactly what a plan item is until its status says otherwise.
          Sits behind the crown with the halo, and takes no pointer events, so
          it can never swallow a click meant for a surface. */}
      {highlighted && (
        <path d={path} fill="none" {...PLANNED_RING} pointerEvents="none" />
      )}

      {/* The crown base. The gradient is ONE shared def in Odontogram.jsx
          (objectBoundingBox units fit it to each crown); ghosts stay flat
          grey — a missing tooth gets no shine. */}
      <path d={path} fill={missing ? outline.fill : `url(#${idPrefix}crownShine)`} />

      {/* ── Decay recorded against the WHOLE tooth ──────────────────────────
          «پوسیدگی در کل دندان هجدهم» used to draw nothing: caries was missing
          from the outline tints AND from the glyph list, so a whole-tooth
          finding landed on a crown that still looked sound. A surface finding
          paints one facet in saturated red; this washes the entire crown in
          the pale field red — the same fact at the scale it was recorded at,
          and the two stay tellable apart at a glance.

          UNDER the surfaces on purpose. Untinted surfaces are transparent, so
          the wash reads through them, while a surface that carries its own
          finding — an existing restoration on a tooth now decaying elsewhere —
          still paints opaquely on top and is not lost. Slightly transparent so
          the crown's shading survives and the tooth keeps its shape. */}
      {!missing && tinted === CONDITION.CARIES && (
        <path
          d={path}
          fill={TOOTH_COLORS.caries_tooth.fill}
          opacity={0.88}
          pointerEvents="none"
        />
      )}

      {missing ? (
        // Struck through, not hidden: the POSITION still matters — it is where
        // a bridge or an implant goes — so removing it would lose the gap.
        <g clipPath={`url(#${clipId})`}>
          <line x1={-w / 2} y1={-h / 2} x2={w / 2} y2={h / 2}
                stroke={outline.stroke} strokeWidth={1.8} />
          <line x1={w / 2} y1={-h / 2} x2={-w / 2} y2={h / 2}
                stroke={outline.stroke} strokeWidth={1.8} />
        </g>
      ) : (
        <g clipPath={`url(#${clipId})`}>
          {Object.entries(byRegion).map(([region, code]) => (
            <polygon
              key={region}
              points={regions[region]}
              fill={fillFor(code)}
              stroke={hoverSurface === code ? CHART_SHADES.surfaceHover : outline.stroke}
              strokeWidth={hoverSurface === code ? 1.1 : 0.4}
              onClick={handleSurface(code)}
              onMouseEnter={() => setHoverSurface(code)}
              onMouseLeave={() => setHoverSurface((s) => (s === code ? null : s))}
              role="button"
              tabIndex={-1}
              aria-label={`${fdi} — ${t(`chart.surface.${code}`)}`}
            />
          ))}
          <rect
            x={regions.centre.x} y={regions.centre.y}
            width={regions.centre.w} height={regions.centre.h}
            rx={2}
            fill={fillFor(centre)}
            stroke={hoverSurface === centre ? CHART_SHADES.surfaceHover : outline.stroke}
            strokeWidth={hoverSurface === centre ? 1.1 : 0.4}
            onClick={handleSurface(centre)}
            onMouseEnter={() => setHoverSurface(centre)}
            onMouseLeave={() => setHoverSurface((s) => (s === centre ? null : s))}
            role="button"
            tabIndex={-1}
            aria-label={`${fdi} — ${t(`chart.surface.${centre}`)}`}
          />

          {/* Cusps and fissures. pointerEvents:none is load-bearing — they sit
              ON the occlusal surface and would otherwise swallow its clicks. */}
          <g pointerEvents="none" opacity={valueOf(centre) ? 0.35 : 0.5}>
            {marks.map((c, i) => (
              <circle key={i} cx={c.cx} cy={c.cy} r={c.r} fill="none"
                      stroke={outline.stroke} strokeWidth={0.7} />
            ))}
            <path d={fissurePath(fdi)} fill="none" stroke={outline.stroke}
                  strokeWidth={0.8} strokeLinecap="round" />
          </g>
        </g>
      )}

      {/* ── Condition glyphs — what was DONE to this tooth ──────────────────
          A tint on the outline is not a record a dentist can read; the glyphs
          are. Drawn in the LOCAL frame so they rotate with the tooth, always
          pointerEvents:none so the five surface hit areas lose nothing, and
          SHAPE carries the meaning — colour only reinforces it, so the chart
          survives colour-blindness. Rendered over ghosts too: an implant or a
          bridge pontic lives exactly where the tooth is NOT. */}
      {anyGlyph && (
        <g pointerEvents="none">
          {has(CONDITION.CROWN) && (
            // The universal chart convention: a second ring just inside the
            // silhouette. Material tints it (teeth.js CROWN_MATERIAL_TINTS).
            <path d={crownRingPath(fdi)} fill="none"
                  stroke={crownRingTint(crownMaterial)} strokeWidth={1.5} />
          )}
          {has(CONDITION.VENEER) && (
            <path d={veneerArcPath(fdi)} fill="none"
                  stroke={TOOTH_COLORS.restoration.stroke}
                  strokeWidth={2.4} strokeLinecap="round" />
          )}
          {has(CONDITION.FRACTURE) && (
            <polyline points={fractureGlyph(fdi)} fill="none"
                      stroke={TOOTH_COLORS.fractured.stroke} strokeWidth={1.4} />
          )}
          {has(CONDITION.IMPLANT) && implantGlyph(fdi).map((s, i) => (
            <line key={i} x1={s.x1} y1={s.y1} x2={s.x2} y2={s.y2}
                  stroke={TOOTH_COLORS.implant.stroke}
                  strokeWidth={1.8} strokeLinecap="round" />
          ))}
          {has(CONDITION.POST_CORE) && (() => {
            const g = postCoreGlyph(fdi);
            return (
              <>
                <line x1={g.bar.x1} y1={g.bar.y1} x2={g.bar.x2} y2={g.bar.y2}
                      stroke={TOOTH_COLORS.post_core.stroke}
                      strokeWidth={1.8} strokeLinecap="round" />
                <circle cx={g.dot.cx} cy={g.dot.cy} r={g.dot.r}
                        fill={TOOTH_COLORS.post_core.stroke} />
              </>
            );
          })()}
          {has(CONDITION.ROOT_CANAL) && (
            // Nudged below the post when both are recorded — RCT → post&core →
            // crown is the classic sequence, and both marks live at the pulp
            // position, so unmoved they would hide each other.
            <polygon
              points={rootCanalGlyph(fdi)}
              fill={TOOTH_COLORS.root_canal.stroke}
              transform={has(CONDITION.POST_CORE)
                ? `translate(0 ${Math.min(w, h) * 0.3})` : undefined}
            />
          )}
        </g>
      )}

      {/* A recorded condition this chart has no mark for — سایش, لقی, ضایعهٔ
          اپیکال and the rest. It gets a small neutral dot, NOT a symbol: the
          tooth admits it is carrying a finding and the hover card names it,
          while inventing a glyph per condition is a clinical convention to
          settle with a dentist rather than in a renderer. Without this the
          tooth is drawn exactly like an examined-and-sound one, which is the
          bug this whole pass is about, one condition over. */}
      {!missing && hasUndrawnCondition(state) && (
        <circle cx={0} cy={h * 0.3} r={1.5} fill={CHART_SHADES.noteMark}
                pointerEvents="none" />
      )}

      {/* The silhouette, drawn last so no surface edge crosses it — which
          also puts it ABOVE the surfaces, so pointerEvents:none is
          load-bearing: without it the stroke catches clicks landing exactly
          on the crown edge and turns a surface click into a tooth click.

          Heavier when a whole-tooth condition tints it: at 1.3 the difference
          between «سالم» grey and a clinical tint was a hairline, which is what
          made a tinted tooth read as "a very small border" rather than as a
          finding. */}
      <path d={path} fill="none" stroke={outline.stroke}
            strokeWidth={tinted ? 2.2 : 1.3} pointerEvents="none" />

      {/* Never examined — distinct from examined-and-sound, which is a real
          clinical statement this must not impersonate. */}
      {!state?.isCharted && !missing && (
        <circle cx={0} cy={0} r={1.5} fill="#cbd5e1" pointerEvents="none" />
      )}

      {/* ── «درمان‌شده» — work was RECORDED on this tooth ──────────────────
          Drawn LAST, so it sits above the silhouette and every surface tint:
          it is the one mark that must survive whatever the tooth's state is,
          including a ghosted missing tooth — an extraction is itself a
          treatment, and the badge is what says somebody did it rather than
          the tooth merely being absent.

          A BADGE and not a second ring. The dashed planned ring already takes
          the outline, and two concentric rings on a phone-sized crown (~10px)
          are one blur; a filled disc at a corner reads at any size and can
          carry a number.

          `pointerEvents: none` for the same reason as the ring: it overlaps
          the crown edge and must never swallow a click meant for a surface. */}
      {treatedCount > 0 && (
        <g pointerEvents="none" transform={`translate(${w * 0.42}, ${-h * 0.42})`}>
          <TreatedBadge count={treatedCount} />
        </g>
      )}
    </g>
  );
}

export { crownSize, toothType };
